import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { SessionService } from "../session.service";
import {
  LogicTreeTest,
  LogicTreeTestListResponse,
  LogicTreeTestsHandlerService,
} from "../handlers/logic-tree-tests-handler.service";
import { LogicTreesHandlerService } from "../handlers/logic-trees-handler.service";

/**
 * Thin facade the flow-tests UI uses so components don't need to know
 * about the underlying handler service or how to resolve the current
 * account/app ids.
 */
@Injectable({ providedIn: "root" })
export class FlowTestsService {
  constructor(
    private handler: LogicTreeTestsHandlerService,
    private logicTreesHandler: LogicTreesHandlerService,
    private session: SessionService,
  ) {}

  private accountId(): number {
    return this.session.currentAccountValue!.accountID;
  }

  private appId(): number | null {
    return this.session.currentAppValue?.id ?? null;
  }

  list(): Observable<LogicTreeTest[]> {
    const appId = this.appId();
    return this.handler
      .listLogicTreeTests(this.accountId(), appId ? { account_app_id: appId } : undefined)
      .pipe(
        map((res: LogicTreeTestListResponse | LogicTreeTest[]) => {
          if (Array.isArray(res)) return res;
          return res?.records || [];
        }),
      );
  }

  get(id: number): Observable<LogicTreeTest> {
    return this.handler.getLogicTreeTest(this.accountId(), id);
  }

  create(logicTreeId: number, config?: Record<string, any>): Observable<LogicTreeTest> {
    return this.handler.createLogicTreeTest(this.accountId(), {
      logic_tree_id: logicTreeId,
      config: config || {},
    });
  }

  remove(id: number): Observable<void> {
    return this.handler.deleteLogicTreeTest(this.accountId(), id);
  }

  /**
   * Flows in the current account scoped to the session's selected
   * facilities. Mirrors the Flow Library query so we get the same set of
   * flows the user normally sees.
   */
  listFlows(): Observable<any> {
    const facilityIDs = (this.session.selectedFacilities || []).map(
      (f: any) => f.id,
    );
    return this.logicTreesHandler.getLogicTrees(this.accountId(), {
      page: 0,
      perPage: 999999999,
      facility_id: facilityIDs,
    });
  }

  getEntryEventTypes(): Observable<any> {
    return this.logicTreesHandler.getEntryEvents(this.accountId());
  }
}
